import React, { useState, useEffect, useCallback } from 'react';
import { useTeam } from '../../../shared/context/teamContext';
import { Plus } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import { IoCodeSlashSharp } from 'react-icons/io5';
import { Modal, message } from 'antd';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import EventDetailsModal from '../../../shared/components/modals/EventDetailsModal';

const CustomEvents = () => {
  const { selectedTeam } = useTeam();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTeamId, setActiveTeamId] = useState(() => {
    try {
      const stored = localStorage.getItem('selectedTeamData');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id || parsed?._id) {
          return parsed.id || parsed._id;
        }
      }
    } catch (error) {
      console.warn('Failed to parse selectedTeamData:', error);
    }
    return null;
  });

  useEffect(() => {
    const synchronizeTeamFromStorage = () => {
      try {
        const stored = localStorage.getItem('selectedTeamData');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?._id && parsed._id !== activeTeamId) {
            setActiveTeamId(parsed._id);
          }
        }
      } catch (error) {
        console.warn('Failed to parse selectedTeamData during sync:', error);
      }
    };

    const handleTeamUpdate = (event) => {
      const newTeam = event.detail?.teamData;
      if (newTeam?._id) {
        setActiveTeamId((prev) => (prev === newTeam._id ? prev : newTeam._id));
      } else {
        synchronizeTeamFromStorage();
      }
    };

    window.addEventListener('teamDataUpdated', handleTeamUpdate);
    synchronizeTeamFromStorage();

    return () => {
      window.removeEventListener('teamDataUpdated', handleTeamUpdate);
    };
  }, [activeTeamId]);

  useEffect(() => {
    if (selectedTeam?._id) {
      setActiveTeamId((prev) => (prev === selectedTeam._id ? prev : selectedTeam._id));
    }
  }, [selectedTeam]);

  const fetchEvents = useCallback(async (teamId) => {
    const targetTeamId = teamId || activeTeamId;
    if (!targetTeamId) return;

    setLoading(true);
    try {
      const response = await axiosInstance.get(`/event-definitions/team/${targetTeamId}`);
      setEvents(response.data.eventDefinitions || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId]);

  useEffect(() => {
    if (!activeTeamId) {
      setEvents([]);
      return;
    }

    setEvents([]);
    fetchEvents(activeTeamId);
  }, [activeTeamId, fetchEvents]);

  const handleRowClick = (event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
  };


  const handleDeleteEvent = (eventId) => {
    const event = events.find(e => e._id === eventId);
    setEventToDelete(event);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      const teamId = activeTeamId || selectedTeam?._id;
      if (!teamId) {
        message.error('Team ID is required to delete the event');
        setDeleting(false);
        return;
      }
      
      await axiosInstance.delete(`/event-definitions/${eventToDelete._id}`, {
        params: { teamId: teamId }
      });
      message.success('Event deleted successfully');
      setDeleteModalVisible(false);
      setEventToDelete(null);
      fetchEvents(teamId);
    } catch (error) {
      console.error('Error deleting event:', error);
      message.error(error.response?.data?.error || 'Error deleting event');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setEventToDelete(null);
  };

  const showCode = (event) => {
    setSelectedEvent(event);
    setShowCodeModal(true);
  };

  const getStatusBadge = (event) => {
    if (event.eventCount > 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold bg-[#D1FAE5] text-[#065F46]">
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-[#FEE2E2] text-[#991B1B]">
        <Icon name="edit_pencil" size={14} color="#991B1B" />
        Integration required
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200 bg-white">
        {/* Page Title and Action Buttons */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Raleway, sans-serif', color: '#1d0c46' }}>
            Custom Events
          </h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all duration-200 shadow-sm hover:shadow-md"
            style={{ fontFamily: 'Raleway, sans-serif', backgroundColor: '#1d0c46' }}
          >
            <Plus size={16} />
            Create new event
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-[#475569]">Loading events...</div>
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-[#475569] mb-2">No events created yet</div>
            <div className="text-sm text-[#475569]">Create your first custom event to start tracking</div>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Count
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Last event date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event) => (
                  <React.Fragment key={event._id}>
                    <tr 
                      className="hover:bg-[#F9FAFB] cursor-pointer transition-colors duration-150"
                      onClick={() => handleRowClick(event)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-[#1F2937]">
                        {event.eventName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {event.websiteName || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(event)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {event.eventType}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {event.eventCount || 0}
                      </td>
                      <td className="px-4 py-4 text-sm text-[#475569] max-w-xs truncate">
                        {event.description || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#475569]">
                        {event.lastEventDate ? new Date(event.lastEventDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              showCode(event);
                            }}
                            className="text-[#475569] hover:text-[#0f172a] transition-colors duration-150"
                            title="View code"
                          >
                            <IoCodeSlashSharp size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEvent(event._id);
                            }}
                            className="text-[#475569] hover:text-[#DC2626] transition-colors duration-150"
                            title="Delete event"
                          >
                            <Icon name="delete" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {events.length > 0 && (
          <div className="flex items-center justify-end gap-4 mt-4 text-sm text-[#475569]">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select className="border border-[#D1D5DB] px-2 py-1 bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent">
                <option>50</option>
                <option>100</option>
              </select>
            </div>
            <div className="text-[#475569]">1-{events.length} of {events.length}</div>
            <div className="flex items-center gap-1">
              <button className="p-1.5 text-[#9CA3AF] cursor-not-allowed hover:bg-gray-100 transition-colors" disabled>
                <span>&lt;</span>
              </button>
              <button className="p-1.5 text-[#9CA3AF] cursor-not-allowed hover:bg-gray-100 transition-colors" disabled>
                <span>&gt;</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          teamId={activeTeamId || selectedTeam?._id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchEvents(activeTeamId || selectedTeam?._id);
          }}
        />
      )}

      {/* Code Modal */}
      {showCodeModal && selectedEvent && (
        <CodeModal
          event={selectedEvent}
          onClose={() => {
            setShowCodeModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Event Details Modal */}
      {showDetailsModal && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          teamId={activeTeamId || selectedTeam?._id}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        title={
          <span style={{ 
            color: '#0f172a',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600
          }}>
            Delete Event
          </span>
        }
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={deleting}
        okText="Delete"
        cancelText="Cancel"
        width={400}
        centered
        styles={{ 
          body: { 
            padding: '20px',
            backgroundColor: '#ffffff'
          },
          header: {
            borderBottom: '1px solid #1d0c46',
            borderRadius: '0px'
          },
          content: {
            borderRadius: '0px',
            border: '1px solid #1d0c46'
          }
        }}
        okButtonProps={{
          style: {
            backgroundColor: '#1d0c46',
            borderColor: '#1d0c46',
            color: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            borderRadius: '0px',
            padding: '4px 16px'
          }
        }}
        cancelButtonProps={{
          style: {
            color: '#0f172a',
            borderColor: '#1d0c46',
            backgroundColor: '#ffffff',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500,
            borderRadius: '0px',
            padding: '4px 16px'
          }
        }}
      >
        <p style={{ 
          fontFamily: 'Raleway, sans-serif',
          fontSize: '16px',
          color: '#0f172a',
          margin: 0
        }}>
          Are you sure you want to delete the event "{eventToDelete?.eventName}"? 
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

// Parameter name validation function (matches server-side rules)
const validateParameterName = (name) => {
  // Empty check
  if (!name || name.trim() === '') {
    return { valid: false, error: 'Parameter name cannot be empty' };
  }

  const trimmed = name.trim();
  
  // Check for leading/trailing whitespace
  if (name !== trimmed) {
    return { valid: false, error: 'Cannot have leading or trailing spaces' };
  }

  // Check length
  if (trimmed.length > 255) {
    return { valid: false, error: 'Parameter name cannot exceed 255 characters' };
  }

  // Check first character
  const firstChar = trimmed[0];
  if (!/^[a-zA-Z_$]/.test(firstChar)) {
    if (/^[0-9]/.test(firstChar)) {
      return { valid: false, error: 'Cannot start with a number. Must start with a letter, underscore (_), or dollar sign ($)' };
    }
    return { valid: false, error: `Cannot start with "${firstChar}". Must start with a letter, underscore (_), or dollar sign ($)` };
  }

  // Check for invalid characters
  if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(trimmed)) {
    // Find invalid characters
    const invalidChars = trimmed.match(/[^a-zA-Z0-9_$]/g);
    if (invalidChars) {
      const uniqueInvalid = [...new Set(invalidChars)];
      const charDisplay = uniqueInvalid.map(c => {
        if (c === ' ') return 'space';
        if (c === '-') return 'hyphen (-)';
        if (c === '.') return 'dot (.)';
        if (c === '@') return '@ symbol';
        return `"${c}"`;
      }).join(', ');
      return { valid: false, error: `Invalid characters: ${charDisplay}. Only letters, numbers, underscores (_), and dollar signs ($) are allowed` };
    }
  }

  // Check for reserved keywords (warning, not error)
  const jsReservedKeywords = ['class', 'if', 'null', 'undefined', 'function', 'return', 'var', 'let', 'const', 'true', 'false', 'break', 'case', 'catch', 'continue', 'debugger', 'default', 'delete', 'do', 'else', 'export', 'extends', 'finally', 'for', 'import', 'in', 'instanceof', 'new', 'super', 'switch', 'this', 'throw', 'try', 'typeof', 'void', 'while', 'with', 'yield'];
  if (jsReservedKeywords.includes(trimmed.toLowerCase())) {
    return { 
      valid: true, 
      warning: `"${trimmed}" is a JavaScript reserved keyword. Consider using "${trimmed}Value" or "user${trimmed.charAt(0).toUpperCase() + trimmed.slice(1)}" instead` 
    };
  }

  // Check for duplicates in existing parameters
  return { valid: true };
};

// Create Event Modal Component
const CreateEventModal = ({ teamId, onClose, onSuccess }) => {
  const [eventName, setEventName] = useState('');
  const [eventType, setEventType] = useState('click');
  const [description, setDescription] = useState('');
  const [parameters, setParameters] = useState([]);
  const [newParamName, setNewParamName] = useState('');
  const [newParamType, setNewParamType] = useState('text');
  const [newParamRequired, setNewParamRequired] = useState(false);
  const [newParamSocialIdentity, setNewParamSocialIdentity] = useState('');
  const [loading, setLoading] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loadingWebsites, setLoadingWebsites] = useState(false);
  const [paramValidation, setParamValidation] = useState({ valid: true, error: null, warning: null });

  // Fetch websites when modal opens
  useEffect(() => {
    const fetchWebsites = async () => {
      if (!teamId) return;
      
      setLoadingWebsites(true);
      try {
        const isObjectId = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
        let response;
        
        if (isObjectId) {
          try {
            response = await axiosInstance.get(`/website/team/${teamId}`);
          } catch (err) {
            const teamName = localStorage.getItem('selectedTeam');
            response = await axiosInstance.get(`/website/name/${teamName}`);
          }
        } else {
          const teamName = localStorage.getItem('selectedTeam') || String(teamId);
          response = await axiosInstance.get(`/website/name/${teamName}`);
        }
        
        if (response?.data?.websites) {
          setWebsites(response.data.websites);
          // Auto-select first website if available
          if (response.data.websites.length > 0) {
            setSelectedSiteId(response.data.websites[0].siteId);
          }
        }
      } catch (error) {
        console.error('Error fetching websites:', error);
      } finally {
        setLoadingWebsites(false);
      }
    };

    fetchWebsites();
  }, [teamId]);

  // Handle event type change - clear parameterz if switching to "click"
  const handleEventTypeChange = (newType) => {
    setEventType(newType);
    if (newType === 'click') {
      // Clear parameters when switching to click type
      setParameters([]);
      setNewParamName('');
      setNewParamType('text');
      setNewParamRequired(false);
      setNewParamSocialIdentity('');
      setParamValidation({ valid: true, error: null, warning: null });
    }
  };

  // Handle parameter name change with validation
  const handleParamNameChange = (value) => {
    setNewParamName(value);
    
    // If empty, clear validation (don't show error, just help text)
    if (!value || value.trim() === '') {
      setParamValidation({ valid: false, error: null, warning: null });
      return;
    }
    
    // Check for duplicate names
    const trimmed = value.trim();
    const isDuplicate = parameters.some(p => p.name === trimmed);
    if (isDuplicate) {
      setParamValidation({ 
        valid: false, 
        error: `"${trimmed}" is already used. Parameter names must be unique.`,
        warning: null 
      });
      return;
    }

    // Validate the name
    const validation = validateParameterName(value);
    setParamValidation(validation);
  };

  const addParameter = () => {
    const trimmed = newParamName.trim();
    
    // Don't add if empty or invalid
    if (!trimmed || !paramValidation.valid) return;
    
    // Check for duplicate one more time
    const isDuplicate = parameters.some(p => p.name === trimmed);
    if (isDuplicate) {
      setParamValidation({ 
        valid: false, 
        error: `"${trimmed}" is already used. Parameter names must be unique.`,
        warning: null 
      });
      return;
    }
    
    setParameters([...parameters, {
      name: trimmed,
      type: newParamType,
      required: newParamRequired,
      socialIdentity: newParamSocialIdentity || null
    }]);
    
    setNewParamName('');
    setNewParamType('text');
    setNewParamRequired(false);
    setNewParamSocialIdentity('');
    setParamValidation({ valid: true, error: null, warning: null });
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSiteId) {
      alert('Please select a website');
      return;
    }
    
    setLoading(true);
    
    try {
      // If click type, always send empty parameters array
      const finalParameters = eventType === 'click' ? [] : parameters;
      
      await axiosInstance.post('/event-definitions', {
        siteId: selectedSiteId,
        eventName,
        eventType,
        description,
        parameters: finalParameters,
        teamId: teamId
      });
      
      onSuccess();
    } catch (error) {
      console.error('Error creating event:', error);
      alert(error.response?.data?.error || 'Error creating event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Create event
          </h2>
          <button onClick={onClose} className="text-[#475569] hover:text-[#475569]">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Website Selection */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Website <span className="text-red-500">*</span>
            </label>
            {loadingWebsites ? (
              <div className="text-sm text-[#475569]">Loading websites...</div>
            ) : websites.length === 0 ? (
              <div className="text-sm text-red-600">No websites available. Please create a website first.</div>
            ) : (
              <select
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937]"
                required
              >
                <option value="">Select a website</option>
                {websites.map((website) => (
                  <option key={website.site_id || website.siteId} value={website.site_id || website.siteId}>
                    {website.domain || website.Domain || website.name || website.Name || website.site_id || website.siteId}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Event Name */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="e.g., Swap Successful"
              className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937] placeholder:text-[#9CA3AF]"
              required
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={eventType}
              onChange={(e) => handleEventTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937]"
            >
              <option value="click">Click</option>
              <option value="form">Form</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When swap is successful"
              rows={3}
              className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937] placeholder:text-[#9CA3AF]"
            />
          </div>

          {/* Add Parameter - Only show for Form events */}
          {eventType === 'form' && (
          <div>
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Add Parameter
            </label>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={newParamName}
                  onChange={(e) => handleParamNameChange(e.target.value)}
                  placeholder="Parameter name"
                  className={`w-full px-3 py-2 border focus:ring-2 focus:border-transparent bg-white text-[#1F2937] placeholder:text-[#9CA3AF] ${
                    paramValidation.error 
                      ? 'border-red-500 focus:ring-red-500' 
                      : paramValidation.warning 
                        ? 'border-yellow-400 focus:ring-yellow-400' 
                        : newParamName.trim() && paramValidation.valid
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-[#D1D5DB] focus:ring-[#1d0c46]'
                  }`}
                />
                {/* Validation feedback */}
                {paramValidation.error && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <span>❌</span>
                    <span>{paramValidation.error}</span>
                  </p>
                )}
                {paramValidation.warning && !paramValidation.error && (
                  <p className="mt-1 text-xs text-yellow-600 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>{paramValidation.warning}</span>
                  </p>
                )}
                {newParamName.trim() && paramValidation.valid && !paramValidation.warning && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <span>✅</span>
                    <span>Valid parameter name</span>
                  </p>
                )}
                {/* Help text when empty */}
                {!newParamName && (
                  <p className="mt-1 text-xs text-[#475569]">
                    Must start with a letter, underscore (_), or dollar sign ($). Only letters, numbers, underscores, and dollar signs allowed.
                  </p>
                )}
              </div>
              <div className="md:w-32 w-full">
                <select
                  value={newParamType}
                  onChange={(e) => setNewParamType(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937]"
                >
                  <option value="text">text</option>
                  <option value="numeric">numeric</option>
                  <option value="boolean">boolean</option>
                </select>
              </div>
              <div className="md:w-40 w-full">
                <select
                  value={newParamSocialIdentity}
                  onChange={(e) => setNewParamSocialIdentity(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D1D5DB] focus:ring-2 focus:ring-[#1d0c46] focus:border-transparent bg-white text-[#1F2937]"
                >
                  <option value="">Social Identity (Optional)</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="phoneno">Phone</option>
                  <option value="telegram">Telegram</option>
                  <option value="discord">Discord</option>
                  <option value="x">X (Twitter)</option>
                  <option value="farcaster">Farcaster</option>
                  <option value="instagram">Instagram</option>
                  <option value="wallet">Wallet Address</option>
                </select>
              </div>
              <label className="flex items-center gap-2 md:px-0 px-3 py-2 md:py-0">
                <input
                  type="checkbox"
                  checked={newParamRequired}
                  onChange={(e) => setNewParamRequired(e.target.checked)}
                />
                <span className="text-sm text-[#475569]">Required</span>
              </label>
              <button
                type="button"
                onClick={addParameter}
                disabled={!paramValidation.valid || !newParamName.trim()}
                className="w-full md:w-auto md:self-start px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] hover:bg-[#361a6f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Parameter
              </button>
            </div>
          </div>
          )}

          {/* Parameters List - Only show for Form events with parameters */}
          {eventType === 'form' && parameters.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Parameters
              </label>
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#475569] uppercase">Parameter name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#475569] uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#475569] uppercase">Social Identity</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-[#475569] uppercase">Required</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {parameters.map((param, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-sm text-[#0f172a]">{param.name}</td>
                      <td className="px-3 py-2 text-sm text-[#475569]">{param.type}</td>
                      <td className="px-3 py-2 text-sm text-[#475569]">
                        {param.socialIdentity ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {param.socialIdentity === 'x' ? 'X (Twitter)' : param.socialIdentity.charAt(0).toUpperCase() + param.socialIdentity.slice(1)}
                          </span>
                        ) : (
                          <span className="text-[#475569]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-[#475569]">{param.required ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeParameter(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="delete" size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#0f172a] bg-white border border-[#D1D5DB] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !eventName || !selectedSiteId}
              className="px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] hover:bg-[#361a6f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Code Modal Component
const CodeModal = ({ event, onClose }) => {
  const [copied, setCopied] = useState(false);

  const generateCode = () => {
    const paramString = event.parameters.map(param => {
      const exampleValue = param.type === 'text' ? `'example'` : 
                           param.type === 'numeric' ? `123` : 
                           param.type === 'boolean' ? `true` : `{}`;
      return `    ${param.name}: ${exampleValue}`;
    }).join(',\n');
    
    // Check if event has eventKey (new format) or use eventName (old format)
    if (event.eventKey) {
      return `// Track ${event.eventName} event
window.Cryptique.track(
  '${event.eventKey}',        // Event Key (unique identifier)
  '${event.eventName}',       // Event Name (for display)
  '${event.eventType}',       // Event Type
  {
${paramString}
  }
);`;
    } else {
      // Fallback for old events without eventKey
      return `// ⚠️ WARNING: This event was created before eventKey was added.
// Please delete and recreate this event, or click "Fix Database" button.

// Track ${event.eventName} event (OLD FORMAT - NOT RECOMMENDED)
window.Cryptique.track(
  'MISSING_EVENT_KEY',        // ⚠️ Event Key is missing!
  '${event.eventName}',       // Event Name
  '${event.eventType}',       // Event Type
  {
${paramString}
  }
);`;
    }
  };

  const code = generateCode();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-2xl w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Integration Code
          </h2>
          <button onClick={onClose} className="text-[#475569] hover:text-[#475569]">
            ✕
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-[#475569] mb-4">
            Copy this code to track "{event.eventName}" events:
          </p>
          <pre className="bg-gray-50 border border-gray-200 p-4 overflow-x-auto text-sm">
            <code>{code}</code>
          </pre>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#0f172a] bg-white border border-[#D1D5DB] hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={copyToClipboard}
            className="px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] hover:bg-[#361a6f] transition-colors"
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomEvents;

